// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import React from 'react'

/**
 * Error boundary for the shipping label (D-SHIP-11). A label render failure
 * must never blank the whole app: the print view still needs to show the
 * shipment reference (so staff can hand-write a label), and the settings page
 * must stay usable so a bad config can be turned back off.
 *
 * Resets whenever `resetKey` changes identity, so editing the config in the
 * settings preview retries the render instead of sticking on the error.
 */

interface Props {
  children: React.ReactNode
  /** Plain-text lines shown in the fallback (e.g. recipient, address, barcode value). */
  fallbackLines?: (string | null | undefined)[]
  resetKey?: unknown
}

interface State { error: Error | null }

export default class LabelErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State { return { error } }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null })
    }
  }

  render() {
    if (!this.state.error) return this.props.children
    const lines = (this.props.fallbackLines ?? []).filter((l): l is string => !!l)
    return (
      <div style={{ border: '2px solid #dc2626', borderRadius: 6, padding: 16, maxWidth: 380, background: '#fff', color: '#111' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#dc2626' }}>Label could not be rendered</div>
        <div style={{ fontSize: 11.5, color: '#555', marginTop: 4 }}>
          The shipment itself is unaffected. Shipment details below; please report this to support.
        </div>
        {lines.length > 0 && (
          <div style={{ marginTop: 10, fontSize: 14, lineHeight: 1.5, fontFamily: 'Courier New, monospace' }}>
            {lines.map((l, i) => <div key={i}>{l}</div>)}
          </div>
        )}
      </div>
    )
  }
}
